'use server'

import { apiRequest, ApiResponse } from '../request'

export type DischargeStatus =
    | 'DISCH_PENDING'
    | 'DISCH_AWAITING_APPROVAL'
    | 'DISCH_APPROVED'
    | 'DISCH_IN_PROGRESS'
    | 'DISCH_COMPLETED'
    | 'DISCH_CANCELLED'

export type DischargeType =
    | 'DISCHARGE_HOME'
    | 'DISCHARGE_TRANSFER'
    | 'DISCHARGE_AGAINST_ADVICE'
    | 'DISCHARGE_DEATH'
    | 'DISCHARGE_ESCAPE'
    | 'DISCHARGE_OTHER'

export interface HospitalDischarge {
    id: number
    companyId: number
    hospitalId: number
    company?: {
        id: number
        name: string
    }
    admissionId: number
    admission?: {
        id: number
        unit?: {
            id: number
            name: string
        }
        bed?: {
            id: number
            number: string
        }
    }
    clientId: number
    client?: {
        id: number
        name: string
        cpf?: string
    }
    dischargeType: DischargeType
    status: DischargeStatus
    requestedAt: string
    requestedById?: number
    requestedBy?: {
        id: number
        name: string
    }
    approvedAt?: string
    approvedById?: number
    approvedBy?: {
        id: number
        name: string
    }
    completedAt?: string
    completedById?: number
    completedBy?: {
        id: number
        name: string
    }
    dischargeDiagnosis?: string
    cid10Code?: string
    clinicalSummary?: string
    orders?: string
    medications?: string
    followUpInstructions?: string
    dietInstructions?: string
    activityRestrictions?: string
    warningSignals?: string
    returnAppointment?: string
    referrals?: string
    physicianId?: number
    physician?: {
        id: number
        name: string
        registrationNumber?: string
    }
    signedAt?: string
    signatureHash?: string
    responsibleName?: string
    responsibleRelation?: string
    responsibleDocument?: string
    responsiblePhone?: string
    patientSignature?: string
    responsibleSignature?: string
    belongingsChecklist?: any
    documentsDelivered?: any
    notes?: string
    createdAt: string
    updatedAt: string
}

export interface CreateDischargeData {
    admissionId: number
    clientId: number
    dischargeType: DischargeType
    dischargeDiagnosis?: string
    cid10Code?: string
    clinicalSummary?: string
    orders?: string
    medications?: string
    followUpInstructions?: string
    dietInstructions?: string
    activityRestrictions?: string
    warningSignals?: string
    returnAppointment?: string
    referrals?: string
    physicianId?: number
    notes?: string
}

export interface UpdateDischargeData extends Partial<CreateDischargeData> {}

export interface CompleteDischargeData {
    responsibleName: string
    responsibleRelation?: string
    responsibleDocument?: string
    responsiblePhone?: string
    patientSignature?: string
    responsibleSignature?: string
    belongingsChecklist?: any
    documentsDelivered?: any
    notes?: string
}

export interface DischargeFilters {
    hospitalId?: number
    status?: DischargeStatus
    startDate?: string
    endDate?: string
}

export async function getDischarges(
    companyId?: number,
    filters?: DischargeFilters
): Promise<ApiResponse<HospitalDischarge[]>> {
    return apiRequest<HospitalDischarge[]>('hospital/discharge', {
        params: { companyId, ...filters },
    })
}

export async function getDischarge(
    id: number
): Promise<ApiResponse<HospitalDischarge>> {
    return apiRequest<HospitalDischarge>(`hospital/discharge/${id}`, {})
}

export async function getPendingDischarges(
    companyId: number
): Promise<ApiResponse<HospitalDischarge[]>> {
    return apiRequest<HospitalDischarge[]>(
        `hospital/discharge/pending/${companyId}`,
        {}
    )
}

export async function getTodayDischarges(
    companyId: number
): Promise<ApiResponse<HospitalDischarge[]>> {
    return apiRequest<HospitalDischarge[]>(
        `hospital/discharge/today/${companyId}`,
        {}
    )
}

export async function createDischarge(
    data: CreateDischargeData
): Promise<ApiResponse<HospitalDischarge>> {
    return apiRequest<HospitalDischarge>('hospital/discharge', { data }, 'post')
}

export async function updateDischarge(
    id: number,
    data: UpdateDischargeData
): Promise<ApiResponse<HospitalDischarge>> {
    return apiRequest<HospitalDischarge>(
        `hospital/discharge/${id}`,
        { data },
        'patch'
    )
}

export async function approveDischarge(
    id: number
): Promise<ApiResponse<HospitalDischarge>> {
    return apiRequest<HospitalDischarge>(
        `hospital/discharge/${id}/approve`,
        {},
        'patch'
    )
}

export async function completeDischarge(
    id: number,
    data: CompleteDischargeData
): Promise<ApiResponse<HospitalDischarge>> {
    return apiRequest<HospitalDischarge>(
        `hospital/discharge/${id}/complete`,
        { data },
        'patch'
    )
}

export async function cancelDischarge(
    id: number,
    reason?: string
): Promise<ApiResponse<HospitalDischarge>> {
    return apiRequest<HospitalDischarge>(
        `hospital/discharge/${id}/cancel`,
        { params: { reason } },
        'patch'
    )
}

export async function signDischarge(
    id: number
): Promise<ApiResponse<HospitalDischarge>> {
    return apiRequest<HospitalDischarge>(
        `hospital/discharge/${id}/sign`,
        {},
        'post'
    )
}

export async function deleteDischarge(id: number): Promise<ApiResponse<void>> {
    return apiRequest<void>(`hospital/discharge/${id}`, {}, 'delete')
}

export const DISCHARGE_STATUS_LABELS: Record<DischargeStatus, string> = {
    DISCH_PENDING: 'Pendente',
    DISCH_AWAITING_APPROVAL: 'Aguardando Aprovação',
    DISCH_APPROVED: 'Aprovada',
    DISCH_IN_PROGRESS: 'Em Andamento',
    DISCH_COMPLETED: 'Concluída',
    DISCH_CANCELLED: 'Cancelada',
}

export const DISCHARGE_STATUS_COLORS: Record<DischargeStatus, string> = {
    DISCH_PENDING: 'badge-warning',
    DISCH_AWAITING_APPROVAL: 'badge-info',
    DISCH_APPROVED: 'badge-secondary',
    DISCH_IN_PROGRESS: 'badge-secondary',
    DISCH_COMPLETED: 'badge-success',
    DISCH_CANCELLED: 'badge-error',
}

export const DISCHARGE_TYPE_LABELS: Record<DischargeType, string> = {
    DISCHARGE_HOME: 'Alta para Casa',
    DISCHARGE_TRANSFER: 'Transferência',
    DISCHARGE_AGAINST_ADVICE: 'Alta a Pedido',
    DISCHARGE_DEATH: 'Óbito',
    DISCHARGE_ESCAPE: 'Evasão',
    DISCHARGE_OTHER: 'Outro',
}
